import {useState, useRef, useEffect, useCallback} from 'react';
import I18n from 'i18n-js';

const errorMessage = error => {
  if (!error) return I18n.t('adminTools.unknownError');
  if (typeof error === 'string') return error;
  return error.message || I18n.t('adminTools.unknownError');
};

const attempt = async (onSave, settingKey, next) => {
  try {
    return await onSave(settingKey, next);
  } catch (e) {
    return {ok: false, error: e};
  }
};

const useSettingSave = ({settingKey, value, onSave, onSaveStateChange}) => {
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const lastSaved = useRef(value);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const reportRef = useRef(onSaveStateChange);
  reportRef.current = onSaveStateChange;

  useEffect(() => {
    if (reportRef.current) reportRef.current(settingKey, {error});
  }, [error, settingKey]);

  useEffect(
    () => () => {
      if (reportRef.current) reportRef.current(settingKey, {error: null});
    },
    [settingKey]
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    lastSaved.current = value;
    if (!inFlight.current) setDraft(value);
  }, [value]);

  const save = useCallback(
    async next => {
      if (inFlight.current) return;
      inFlight.current = true;
      setDraft(next);
      setPending(true);
      setError(null);
      const result = await attempt(onSave, settingKey, next);
      inFlight.current = false;
      if (!mounted.current) return;
      setPending(false);
      if (result && result.ok === true) {
        lastSaved.current = next;
        return;
      }
      setDraft(lastSaved.current);
      if (result && result.cancelled) return;
      setError(
        I18n.t('adminTools.saveFailed', {
          error: errorMessage(result && result.error)
        })
      );
    },
    [settingKey, onSave]
  );

  return {draft, pending, error, save};
};

export default useSettingSave;
